import React, { useState } from 'react';
import {
  LayoutDashboard, ListTodo, BarChart3, ChevronDown, Plus, LogOut, User, ShieldCheck,
} from 'lucide-react';
import machoMark from '../assets/macho-mark.png';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Label } from './ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { Textarea } from './ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { useBoards } from '../context/BoardContext';
import { useAuth } from '../context/AuthContext';
import { TIERS } from '../lib/categories';
import { toast } from '../hooks/use-toast';
import ProfileDialog from './ProfileDialog';

const Logo = () => (
  <div className="flex items-center gap-2.5" data-testid="topbar-logo">
    <img
      src={machoMark}
      alt="Macho"
      className="h-9 w-auto"
      data-testid="topbar-macho-mark"
    />
    <div>
      <div className="text-[19px] font-bold text-slate-900 leading-none tracking-tight" data-testid="topbar-app-name">
        Genba Pulse
      </div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">
        SQPDCHI
      </div>
    </div>
  </div>
);

const NewBoardDialog = ({ open, onOpenChange }) => {
  const { addBoard, currentUserEmail } = useBoards();
  const [form, setForm] = useState({
    name: '',
    description: '',
    owner_email: currentUserEmail || '',
    tier: 'team',
  });
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    setForm((f) => ({ ...f, owner_email: currentUserEmail || f.owner_email }));
  }, [currentUserEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.owner_email) {
      toast({ title: 'Missing fields', description: 'Board name and owner email are required.' });
      return;
    }
    try {
      setSubmitting(true);
      await addBoard(form);
      toast({ title: 'Board created' });
      onOpenChange(false);
      setForm({ name: '', description: '', owner_email: currentUserEmail || '', tier: 'team' });
    } catch (err) {
      toast({ title: 'Failed', description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create a new board</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Board name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Assembly Line A"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Owner email</Label>
            <Input
              type="email"
              value={form.owner_email}
              onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Tier</Label>
            <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIERS.map((t) => (<SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="mt-1.5"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#0090D8] hover:bg-[#0079B5] text-white"
            >
              {submitting ? 'Creating...' : 'Create board'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const BoardSwitcher = () => {
  const { boards, activeBoard, selectBoard } = useBoards();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="h-10 min-w-[220px] justify-between border-slate-200 bg-white"
          >
            <span className="truncate text-left">
              {activeBoard ? activeBoard.name : 'No board selected'}
            </span>
            <ChevronDown className="h-4 w-4 ml-2 text-slate-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[280px]">
          <DropdownMenuLabel>Your boards</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {boards.length === 0 && (
            <div className="px-2 py-3 text-sm text-slate-500">No boards yet</div>
          )}
          {boards.map((b) => (
            <DropdownMenuItem
              key={b.id}
              onClick={() => selectBoard(b)}
              className={activeBoard?.id === b.id ? 'bg-sky-50 text-[#0090D8]' : ''}
            >
              <div className="flex flex-col gap-0.5">
                <div className="font-medium">{b.name}</div>
                <div className="text-xs text-slate-500">
                  {b.tier} · {b.owner_email}
                </div>
              </div>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create new board
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <NewBoardDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
};

const UserMenu = ({ onNavigateAdmin }) => {
  const { user, logout } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  if (!user) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 h-10 px-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50 transition-colors">
            {user.picture ? (
              
              <img
                src={user.picture}
                alt="Profile picture"
                className="w-7 h-7 rounded-full"
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center text-white text-xs font-semibold">
                {(user.name || user.email)[0].toUpperCase()}
              </div>
            )}
            <span className="text-sm text-slate-700 max-w-[140px] truncate hidden md:inline">
              {user.display_name || user.name || user.email}
            </span>
            <ChevronDown className="h-4 w-4 text-slate-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[240px]">
          <DropdownMenuLabel>
            <div className="flex flex-col gap-0.5">
              <div className="font-semibold text-sm text-slate-900 flex items-center gap-1.5">
                {user.display_name || user.name}
                {user.role === 'admin' && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 uppercase tracking-wider">
                    Admin
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500">{user.email}</div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setProfileOpen(true)}>
            <User className="h-4 w-4 mr-2" />
            Profile settings
          </DropdownMenuItem>
          {user.role === 'admin' && onNavigateAdmin && (
            <DropdownMenuItem onClick={onNavigateAdmin}>
              <ShieldCheck className="h-4 w-4 mr-2" />
              User management
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-red-600">
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
};

const NAV_ITEMS = [
  { key: 'board', label: 'Board', icon: LayoutDashboard },
  { key: 'tasks', label: 'Open Issues', icon: ListTodo },
  { key: 'analytics', label: 'Analytics', icon: BarChart3 },
];

const TopBar = ({ activeView, onViewChange }) => {
  const onNavigateAdmin = () => onViewChange('admin');
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
      <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <Logo />
          <div className="hidden md:block h-8 w-px bg-slate-200" />
          <div className="hidden md:block">
            <BoardSwitcher />
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-1 bg-slate-50 p-1 rounded-lg border border-slate-200">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.key;
            return (
              <button
                key={item.key}
                onClick={() => onViewChange(item.key)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'bg-white text-[#0090D8] shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="flex items-center gap-2">
          <UserMenu onNavigateAdmin={onNavigateAdmin} />
        </div>
      </div>
      <div className="md:hidden px-4 pb-3 space-y-2">
        <BoardSwitcher />
        <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-lg border border-slate-200 overflow-x-auto">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.key;
            return (
              <button
                key={item.key}
                onClick={() => onViewChange(item.key)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap ${
                  isActive ? 'bg-white text-[#0090D8] shadow-sm' : 'text-slate-600'
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </button>
            );
          })}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
